/**
 * Small reusable handler for mailto:, tel: and wa.me contact link clicks.
 * Fires the GTM `contact_click` event without ever sending the raw email,
 * phone number, or WhatsApp handle. Only the transport method + a UUID
 * are pushed to the dataLayer.
 *
 * The handler is intentionally passive: it does not preventDefault, does
 * not stopPropagation, and any tracking failure is swallowed so a broken
 * vendor cannot block the browser's default mailto/tel/whatsapp handoff.
 */

import { trackEvent, generateEventId } from '@/lib/tracking';

/**
 * Infer the transport method from an anchor's href.
 * Returns 'email' | 'phone' | 'whatsapp' | null.
 */
export const inferContactMethod = (href) => {
  if (typeof href !== 'string') return null;
  const h = href.trim().toLowerCase();
  if (h.startsWith('mailto:')) return 'email';
  if (h.startsWith('tel:')) return 'phone';
  if (h.startsWith('http://wa.me') || h.startsWith('https://wa.me')) return 'whatsapp';
  if (h.startsWith('http://api.whatsapp.com') || h.startsWith('https://api.whatsapp.com')) return 'whatsapp';
  return null;
};

/**
 * Fire a `contact_click` event. Safe to call anywhere; a tracking failure
 * cannot break navigation because trackEvent wraps every vendor call.
 */
export const trackContactClick = (method) => {
  if (!method) return;
  try {
    trackEvent('contact_click', {
      method,
      event_id: generateEventId(),
    });
  } catch {
    /* tracking never breaks the user flow */
  }
};

/**
 * Convenience `onClick` handler factory for JSX anchors:
 *
 *   <a href="mailto:x@y.co.za" onClick={onContactLinkClick}>…</a>
 *
 * Reads the current target's href, infers the method, and fires the
 * event. No preventDefault, so the browser still opens the mail client
 * / dialer / WhatsApp deep link.
 */
export const onContactLinkClick = (e) => {
  try {
    const href = e?.currentTarget?.getAttribute?.('href');
    trackContactClick(inferContactMethod(href));
  } catch {
    /* no-op */
  }
};
